import {
  ApiError,
  createClientRemote,
  createComplianceRemote,
  createExpenseRemote,
  pushClientXeroRemote,
  deleteComplianceRemote,
  deleteExpenseRemote,
  deleteJobRemote,
  fetchAllJobs,
  fetchCompliance,
  fetchExpenses,
  fetchReceipts,
  clearManualResolveRemote,
  flushReceiptAccpayQueueRemote,
  setManualResolveRemote,
  isPermanentHttp,
  patchComplianceRemote,
  patchExpenseRemote,
  patchReceiptRemote,
  postJobsBatch,
  renewComplianceRemote,
  uploadReceiptRemote,
  type BatchJobPayload,
} from '../api/client.js';
import {
  getCompliance,
  listComplianceBySync,
  mergeComplianceFromServer,
  putComplianceLocal,
  type LocalCompliance,
} from '../db/complianceDb.js';
import {
  getExpense,
  listExpensesBySync,
  mergeExpensesFromServer,
  putExpenseLocal,
  type LocalExpense,
} from '../db/expensesDb.js';
import {
  deleteJobLocal,
  getJob,
  listClientsCached,
  listJobsBySync,
  mergeJobsFromServer,
  putClientCached,
  putJobLocal,
  type CachedClient,
  type LocalJob,
} from '../db/jobsDb.js';
import {
  getReceiptLocal,
  listReceiptsBySync,
  listReceiptsLocal,
  mergeReceiptsFromServer,
  PHOTO_LOST_REASON,
  putReceiptLocal,
  receiptPhotoLost,
} from '../db/receiptsDb.js';
import { patchSyncFreshness } from './freshness.js';

export type FlushSummary = {
  attempted: number;
  synced: number;
  failed: number;
  deferredAuth: boolean;
  transient: boolean;
};

let flushing = false;
let backoffMs = 0;
let nextAllowedAt = 0;

const MAX_BACKOFF = 5 * 60_000;

function toPayload(job: LocalJob): BatchJobPayload {
  return {
    id: job.id,
    work_date: job.work_date,
    client_id: job.client_id,
    work_type_id: job.work_type_id,
    duration_type: job.duration_type,
    custom_hours: job.custom_hours,
    custom_amount: job.custom_amount,
    site_reference: job.site_reference,
    site_ids: job.site_ids ?? [],
    billable: job.billable !== false,
    status: job.status ?? 'completed',
    start_time: job.start_time,
    po_number: job.po_number,
    notes: job.notes,
  };
}

function compliancePayload(item: LocalCompliance): Record<string, unknown> {
  return {
    id: item.id,
    category: item.category,
    title: item.title,
    subject_reference: item.subject_reference,
    issuer: item.issuer,
    issued_date: item.issued_date,
    expiry_date: item.expiry_date,
    lead_time_days: item.lead_time_days,
    cost_pence: item.cost_pence,
    billing_frequency: item.billing_frequency,
    notes: item.notes,
    document_reference: item.document_reference,
    active: item.active,
  };
}

function expensePayload(item: LocalExpense): Record<string, unknown> {
  return {
    id: item.id,
    name: item.name,
    category: item.category,
    amount_pence: item.amount_pence,
    frequency: item.frequency,
    notes: item.notes,
    active: item.active,
  };
}

function plainReason(code: string, message: string): string {
  if (code === 'job_invoiced') {
    return 'This job is on an invoice and cannot be changed.';
  }
  if (code === 'foreign_key') {
    return 'Client or work type is missing on the server. Update lists after signing in.';
  }
  if (code === 'validation_error' || code === 'check_violation') {
    return message || 'The server rejected this entry as invalid.';
  }
  if (/multipart|Could not read the photo/i.test(message)) {
    return message;
  }
  if (/not supported|HEIC|JPEG|Most Compatible/i.test(message)) {
    return message;
  }
  return message || `Sync failed (${code})`;
}

export function resetSyncBackoff(): void {
  backoffMs = 0;
  nextAllowedAt = 0;
}

async function flushReceiptAccpayQueue(summary: FlushSummary): Promise<void> {
  try {
    await flushReceiptAccpayQueueRemote();
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      summary.deferredAuth = true;
      summary.transient = true;
      return;
    }
    console.warn('receipt accpay queue flush failed', err);
  }
}

async function flushReceiptUploads(summary: FlushSummary): Promise<void> {
  const queue = await listReceiptsBySync('pending');
  for (const item of queue) {
    // Blob must survive until the server confirms upload. If it is gone,
    // mark a clear data-loss failure — never leave a forever-unsyncable row.
    if (receiptPhotoLost(item)) {
      summary.attempted += 1;
      item.syncState = 'failed';
      item.failReason = PHOTO_LOST_REASON;
      item.updatedAt = new Date().toISOString();
      item.blob = null;
      await putReceiptLocal(item);
      summary.failed += 1;
      console.error('receipt photo lost before upload', { id: item.id });
      continue;
    }
    if (!item.blob) {
      // Synced metadata pending without a photo — should not be in upload queue.
      console.warn('skipping pending receipt without blob', { id: item.id });
      continue;
    }
    summary.attempted += 1;
    item.syncState = 'syncing';
    await putReceiptLocal(item);
    // Keep a firm reference — do not clear until server confirms.
    const blob = item.blob;
    try {
      const remote = await uploadReceiptRemote(item.id, blob, {
        captured_at: item.captured_at,
        mimeType: item.mime_type,
      });
      let final = remote;
      const hasEdits =
        item.supplier_name ||
        item.receipt_date ||
        item.category ||
        item.gross_pence != null ||
        item.net_pence != null ||
        item.vat_pence != null ||
        item.currency ||
        item.notes ||
        item.supplier_vat_number ||
        item.status === 'confirmed';
      if (hasEdits) {
        final = await patchReceiptRemote(item.id, {
          supplier_name: item.supplier_name,
          receipt_date: item.receipt_date,
          net_pence: item.net_pence,
          vat_pence: item.vat_pence,
          gross_pence: item.gross_pence,
          currency: item.currency,
          category: item.category,
          supplier_vat_number: item.supplier_vat_number,
          notes: item.notes,
          ...(item.status === 'confirmed' ? { status: 'confirmed' } : {}),
          ...(item.status === 'confirmed' && item.money_reconcile_overridden
            ? { money_reconcile_overridden: true }
            : {}),
          ...(item.status === 'confirmed' && item.accept_receipt_date
            ? { accept_receipt_date: true }
            : {}),
        });
      }
      if (item.localPreviewUrl) {
        try {
          URL.revokeObjectURL(item.localPreviewUrl);
        } catch (err) {
          console.debug('revokeObjectURL failed', err);
        }
      }
      // Server confirmed — only now release the local photo.
      await putReceiptLocal({
        ...item,
        status: final.status as typeof item.status,
        supplier_name: final.supplier_name,
        receipt_date: final.receipt_date,
        currency: final.currency ?? item.currency ?? null,
        net_pence: final.net_pence,
        vat_pence: final.vat_pence,
        gross_pence: final.gross_pence,
        gbp_net_pence: final.gbp_net_pence ?? null,
        gbp_vat_pence: final.gbp_vat_pence ?? null,
        gbp_gross_pence: final.gbp_gross_pence ?? null,
        fx_rate: final.fx_rate ?? null,
        fx_rate_as_of: final.fx_rate_as_of ?? null,
        fx_source: final.fx_source ?? null,
        fx_period: final.fx_period ?? null,
        category: final.category,
        supplier_vat_number: final.supplier_vat_number ?? null,
        vat_evidence_state: final.vat_evidence_state ?? null,
        notes: final.notes,
        has_thumb: Boolean(final.has_thumb),
        blob: null,
        localPreviewUrl: null,
        syncState: 'synced',
        failReason: null,
        extraction_error: final.extraction_error ?? null,
        extraction_cost_usd: final.extraction_cost_usd ?? null,
        money_reconcile_ok: final.money_reconcile_ok ?? null,
        money_reconcile_note: final.money_reconcile_note ?? null,
        money_reconcile_overridden: final.money_reconcile_overridden ?? false,
        low_confidence_fields: final.low_confidence_fields ?? [],
        fields_edited: final.fields_edited ?? [],
        confidence: final.extraction_suggested?.confidence ?? null,
        rechargeable: final.rechargeable ?? item.rechargeable ?? null,
        client_id: final.client_id ?? item.client_id ?? null,
        site_id: final.site_id ?? item.site_id ?? null,
        recharge_job_id: final.recharge_job_id ?? item.recharge_job_id ?? null,
        recharge_frozen: final.recharge_frozen ?? item.recharge_frozen ?? false,
        xero_bill_id: final.xero_bill_id ?? item.xero_bill_id ?? null,
        xero_attachment_status:
          final.xero_attachment_status ?? item.xero_attachment_status ?? null,
        xero_push_error: final.xero_push_error ?? item.xero_push_error ?? null,
        xero_push_outcome: final.xero_push_outcome ?? item.xero_push_outcome ?? null,
        updatedAt: new Date().toISOString(),
      });
      summary.synced += 1;
    } catch (err) {
      const current = await getReceiptLocal(item.id);
      if (!current) continue;
      // Always preserve blob on failure — getReceiptLocal should still have it;
      // re-attach from the in-memory reference if IDB dropped it.
      if (!current.blob || current.blob.size === 0) {
        current.blob = blob;
      }
      if (err instanceof ApiError) {
        if (err.status === 401 || err.status === 403) {
          current.syncState = 'pending';
          await putReceiptLocal(current);
          summary.deferredAuth = true;
          summary.transient = true;
          return;
        }
        if (err.status === 409) {
          // Server already has this id — confirmed present; safe to drop blob.
          if (current.localPreviewUrl) {
            try {
              URL.revokeObjectURL(current.localPreviewUrl);
            } catch (err) {
              console.debug('revokeObjectURL failed', err);
            }
          }
          current.blob = null;
          current.localPreviewUrl = null;
          current.syncState = 'synced';
          current.status = 'awaiting_review';
          current.failReason = null;
          current.updatedAt = new Date().toISOString();
          await putReceiptLocal(current);
          summary.synced += 1;
          continue;
        }
        if (isPermanentHttp(err.status)) {
          current.syncState = 'failed';
          current.failReason = plainReason(err.code, err.message);
          current.updatedAt = new Date().toISOString();
          await putReceiptLocal(current);
          summary.failed += 1;
          continue;
        }
      }
      current.syncState = 'pending';
      current.failReason =
        err instanceof ApiError
          ? plainReason(err.code, err.message)
          : err instanceof Error
            ? err.message
            : String(err);
      await putReceiptLocal(current);
      summary.transient = true;
      return;
    }
  }

  // Also surface already-failed rows that lost their photo (e.g. older builds).
  const failed = await listReceiptsBySync('failed');
  for (const item of failed) {
    if (receiptPhotoLost(item) && item.failReason !== PHOTO_LOST_REASON) {
      item.failReason = PHOTO_LOST_REASON;
      item.blob = null;
      item.updatedAt = new Date().toISOString();
      await putReceiptLocal(item);
      console.error('receipt photo lost (failed queue)', { id: item.id });
    }
  }
}

function applyRemoteClient(
  local: CachedClient,
  remote: {
    id: string;
    name: string;
    day_rate: number | string;
    half_day_rate: number | string;
    hourly_rate: number | string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    address?: string | null;
    payment_terms_days?: number | null;
    external_contact_id?: string | null;
    xero_contact_status?: CachedClient['xero_contact_status'];
    xero_contact_error?: string | null;
    xero_contact_matches?: CachedClient['xero_contact_matches'];
  },
): CachedClient {
  return {
    ...local,
    id: remote.id,
    name: remote.name,
    day_rate: remote.day_rate ?? null,
    half_day_rate: remote.half_day_rate ?? null,
    hourly_rate: remote.hourly_rate ?? null,
    contact_name: remote.contact_name ?? null,
    contact_email: remote.contact_email ?? null,
    address: remote.address ?? null,
    payment_terms_days: remote.payment_terms_days ?? local.payment_terms_days ?? 30,
    external_contact_id: remote.external_contact_id ?? null,
    xero_contact_status: remote.xero_contact_status ?? local.xero_contact_status,
    xero_contact_error: remote.xero_contact_error ?? null,
    xero_contact_matches: remote.xero_contact_matches ?? null,
    pendingOp: null,
  };
}

async function flushPendingClients(summary: FlushSummary): Promise<void> {
  const queue = (await listClientsCached()).filter(
    (c) => c.pendingOp === 'create' || c.pendingOp === 'xero_push',
  );
  for (const item of queue) {
    summary.attempted += 1;
    try {
      const remote =
        item.pendingOp === 'xero_push'
          ? await pushClientXeroRemote(item.id)
          : await createClientRemote({
              id: item.id,
              name: item.name,
              day_rate: Number(item.day_rate),
              half_day_rate: Number(item.half_day_rate),
              hourly_rate:
                item.hourly_rate == null ? null : Number(item.hourly_rate),
              contact_name: item.contact_name ?? null,
              contact_email: item.contact_email ?? null,
              address: item.address ?? null,
              payment_terms_days: item.payment_terms_days ?? 30,
            });
      await putClientCached(applyRemoteClient(item, remote));
      summary.synced += 1;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        summary.deferredAuth = true;
        summary.transient = true;
        return;
      }
      if (err instanceof ApiError && isPermanentHttp(err.status)) {
        await putClientCached({
          ...item,
          pendingOp: null,
          xero_contact_status: 'failed',
          xero_contact_error: plainReason(err.code, err.message),
        });
        summary.failed += 1;
        continue;
      }
      summary.transient = true;
      return;
    }
  }
}

async function flushComplianceOps(summary: FlushSummary): Promise<void> {
  const queue = (await listComplianceBySync('pending')).filter((c) => c.pendingOp);
  for (const item of queue) {
    summary.attempted += 1;
    item.syncState = 'syncing';
    await putComplianceLocal(item);
    try {
      if (item.pendingOp === 'create') {
        await createComplianceRemote(compliancePayload(item));
      } else if (item.pendingOp === 'update') {
        const payload = compliancePayload(item);
        delete payload.id;
        await patchComplianceRemote(item.id, payload);
      } else if (item.pendingOp === 'delete') {
        await deleteComplianceRemote(item.id);
      } else if (item.pendingOp === 'renew') {
        await renewComplianceRemote(item.id, {
          new_expiry_date: item.renewNewExpiry,
        });
      }
      const current = await getCompliance(item.id);
      if (!current) continue;
      if (item.pendingOp === 'delete') {
        current.deleted_at = current.deleted_at ?? new Date().toISOString();
        current.active = false;
      }
      current.syncState = 'synced';
      current.pendingOp = null;
      current.renewNewExpiry = null;
      current.failReason = null;
      current.updatedAt = new Date().toISOString();
      await putComplianceLocal(current);
      summary.synced += 1;
    } catch (err) {
      const current = await getCompliance(item.id);
      if (!current) continue;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        current.syncState = 'pending';
        await putComplianceLocal(current);
        summary.deferredAuth = true;
        summary.transient = true;
        return;
      }
      if (err instanceof ApiError && isPermanentHttp(err.status)) {
        current.syncState = 'failed';
        current.failReason = plainReason(err.code, err.message);
        await putComplianceLocal(current);
        summary.failed += 1;
        continue;
      }
      current.syncState = 'pending';
      await putComplianceLocal(current);
      summary.transient = true;
      return;
    }
  }
}

async function flushExpenseOps(summary: FlushSummary): Promise<void> {
  const queue = (await listExpensesBySync('pending')).filter((e) => e.pendingOp);
  for (const item of queue) {
    summary.attempted += 1;
    item.syncState = 'syncing';
    await putExpenseLocal(item);
    try {
      if (item.pendingOp === 'create') {
        await createExpenseRemote(expensePayload(item));
      } else if (item.pendingOp === 'update') {
        const payload = expensePayload(item);
        delete payload.id;
        await patchExpenseRemote(item.id, payload);
      } else if (item.pendingOp === 'delete') {
        await deleteExpenseRemote(item.id);
      }
      const current = await getExpense(item.id);
      if (!current) continue;
      if (item.pendingOp === 'delete') {
        current.deleted_at = current.deleted_at ?? new Date().toISOString();
        current.active = false;
      }
      current.syncState = 'synced';
      current.pendingOp = null;
      current.failReason = null;
      current.updatedAt = new Date().toISOString();
      await putExpenseLocal(current);
      summary.synced += 1;
    } catch (err) {
      const current = await getExpense(item.id);
      if (!current) continue;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        current.syncState = 'pending';
        await putExpenseLocal(current);
        summary.deferredAuth = true;
        summary.transient = true;
        return;
      }
      if (err instanceof ApiError && isPermanentHttp(err.status)) {
        current.syncState = 'failed';
        current.failReason = plainReason(err.code, err.message);
        await putExpenseLocal(current);
        summary.failed += 1;
        continue;
      }
      current.syncState = 'pending';
      await putExpenseLocal(current);
      summary.transient = true;
      return;
    }
  }
}

async function flushJobDeletes(summary: FlushSummary): Promise<boolean> {
  const deletes = (await listJobsBySync('pending')).filter(
    (j) => j.pendingOp === 'delete',
  );
  for (const job of deletes) {
    summary.attempted += 1;
    job.syncState = 'syncing';
    job.lastSyncAttemptAt = new Date().toISOString();
    await putJobLocal(job);
    try {
      await deleteJobRemote(job.id);
      await deleteJobLocal(job.id);
      summary.synced += 1;
    } catch (err) {
      const current = await getJob(job.id);
      if (!current) continue;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        current.syncState = 'pending';
        await putJobLocal(current);
        summary.deferredAuth = true;
        summary.transient = true;
        return false;
      }
      if (err instanceof ApiError && isPermanentHttp(err.status)) {
        current.syncState = 'failed';
        current.failReason = plainReason(err.code, err.message);
        current.updatedAt = new Date().toISOString();
        await putJobLocal(current);
        summary.failed += 1;
        continue;
      }
      current.syncState = 'pending';
      await putJobLocal(current);
      summary.transient = true;
      return false;
    }
  }
  return true;
}

/**
 * Manual resolve is queued on the local row, separate from the photo upload.
 * A pending upload is flushed first; this pass then writes only the three columns.
 */
async function flushManualResolves(summary: FlushSummary): Promise<void> {
  const queued = (await listReceiptsLocal()).filter(
    (item) => item.manual_resolve_pending,
  );
  for (const item of queued) {
    if (
      item.manual_resolve_pending === 'set' &&
      item.syncState !== 'synced' &&
      item.blob
    ) {
      continue;
    }
    summary.attempted += 1;
    try {
      const remote =
        item.manual_resolve_pending === 'clear'
          ? await clearManualResolveRemote(item.id)
          : await setManualResolveRemote(item.id, {
              reason: item.manual_resolve_reason ?? '',
              note: item.manual_resolve_note ?? null,
            });
      const current = await getReceiptLocal(item.id);
      if (!current || current.manual_resolve_pending !== item.manual_resolve_pending) {
        continue;
      }
      await putReceiptLocal({
        ...current,
        manual_resolved_at: remote.manual_resolved_at ?? null,
        manual_resolve_reason: remote.manual_resolve_reason ?? null,
        manual_resolve_note: remote.manual_resolve_note ?? null,
        manual_resolve_pending: null,
        updatedAt: new Date().toISOString(),
      });
      summary.synced += 1;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        summary.deferredAuth = true;
        summary.transient = true;
        return;
      }
      if (err instanceof ApiError && isPermanentHttp(err.status)) {
        const current = await getReceiptLocal(item.id);
        if (
          current &&
          current.manual_resolve_pending === item.manual_resolve_pending
        ) {
          current.manual_resolve_pending = null;
          if (item.manual_resolve_pending === 'set') {
            current.manual_resolved_at = null;
            current.manual_resolve_reason = null;
            current.manual_resolve_note = null;
          }
          current.updatedAt = new Date().toISOString();
          await putReceiptLocal(current);
        }
        summary.failed += 1;
        continue;
      }
      summary.transient = true;
      return;
    }
  }
}

/**
 * Flush pending jobs via POST /jobs/batch, then compliance mutations.
 * Never throws to callers in a way that should block the form.
 */
export async function flushPendingJobs(): Promise<FlushSummary> {
  const empty: FlushSummary = {
    attempted: 0,
    synced: 0,
    failed: 0,
    deferredAuth: false,
    transient: false,
  };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return empty;
  }
  if (flushing) return empty;
  if (Date.now() < nextAllowedAt) return empty;

  flushing = true;
  const summary: FlushSummary = { ...empty };

  try {
    // Receipts first — never let a stuck job queue starve photo uploads.
    await flushReceiptUploads(summary);
    if (summary.deferredAuth) {
      summary.transient = true;
      return summary;
    }
    await flushManualResolves(summary);
    if (summary.deferredAuth) {
      summary.transient = true;
      return summary;
    }
    await flushReceiptAccpayQueue(summary);
    const receiptsTransient = summary.transient;
    // Clear so job-layer transient flags don't get confused with receipt ones;
    // we OR them back before deciding whether to stop early.
    summary.transient = false;

    await flushPendingClients(summary);
    if (summary.deferredAuth) {
      summary.transient = true;
      return summary;
    }

    const deletesOk = await flushJobDeletes(summary);
    if (summary.deferredAuth) {
      summary.transient = true;
      return summary;
    }

    if (deletesOk && !summary.transient) {
      const pending = (await listJobsBySync('pending')).filter(
        (j) => j.pendingOp !== 'delete',
      );
      if (pending.length > 0) {
        summary.attempted += pending.length;
        const now = new Date().toISOString();

        for (const job of pending) {
          job.syncState = 'syncing';
          job.lastSyncAttemptAt = now;
          await putJobLocal(job);
        }

        let results;
        try {
          results = await postJobsBatch(pending.map(toPayload));
        } catch (err) {
          const message =
            err instanceof ApiError
              ? plainReason(err.code, err.message)
              : err instanceof Error
                ? err.message
                : String(err);
          console.error('jobs batch POST failed', err);
          const permanent =
            err instanceof ApiError && isPermanentHttp(err.status);

          for (const job of pending) {
            const current = await getJob(job.id);
            if (current && current.syncState === 'syncing') {
              current.failReason = message;
              current.syncState = permanent ? 'failed' : 'pending';
              current.updatedAt = new Date().toISOString();
              await putJobLocal(current);
              if (permanent) summary.failed += 1;
            }
          }

          if (
            err instanceof ApiError &&
            (err.status === 401 || err.status === 403)
          ) {
            summary.deferredAuth = true;
            summary.transient = true;
            return summary;
          }

          if (!permanent) {
            summary.transient = true;
          }
          // Permanent job failures: continue to compliance/merge below.
          results = null;
        }

        if (results) {
          const seen = new Set<string>();
          for (const item of results) {
            const job = await getJob(item.id);
            if (!job) continue;
            seen.add(item.id);

            if (item.ok) {
              job.syncState = 'synced';
              job.pendingOp = null;
              job.failReason = null;
              job.invoice_run_id =
                item.data?.invoice_run_id ?? job.invoice_run_id;
              job.updatedAt = new Date().toISOString();
              await putJobLocal(job);
              summary.synced += 1;
              continue;
            }

            const code = item.error?.code ?? 'error';
            const message = item.error?.message ?? 'Sync failed';
            job.syncState = 'failed';
            job.failReason = plainReason(code, message);
            job.updatedAt = new Date().toISOString();
            await putJobLocal(job);
            summary.failed += 1;
          }

          for (const job of pending) {
            if (seen.has(job.id)) continue;
            const current = await getJob(job.id);
            if (current && current.syncState === 'syncing') {
              current.syncState = 'pending';
              await putJobLocal(current);
            }
          }
        }
      }
    }

    summary.transient = summary.transient || receiptsTransient;
    if (summary.deferredAuth || summary.transient) {
      if (summary.transient && !summary.deferredAuth) {
        backoffMs = backoffMs === 0 ? 2000 : Math.min(MAX_BACKOFF, backoffMs * 2);
        nextAllowedAt = Date.now() + backoffMs;
      }
      // Still merge remote receipts so history updates even when jobs are stuck.
      try {
        const receipts = await fetchReceipts({ limit: '100' });
        await mergeReceiptsFromServer(receipts);
        await patchSyncFreshness({
          receiptsAt: new Date().toISOString(),
          receiptsError: null,
        });
      } catch (err) {
        console.warn('receipts merge failed; showing cached data', err);
        await patchSyncFreshness({
          receiptsError: err instanceof Error ? err.message : String(err),
        });
      }
      return summary;
    }

    await flushComplianceOps(summary);
    if (summary.deferredAuth || summary.transient) {
      if (summary.transient && !summary.deferredAuth) {
        backoffMs = backoffMs === 0 ? 2000 : Math.min(MAX_BACKOFF, backoffMs * 2);
        nextAllowedAt = Date.now() + backoffMs;
      }
      return summary;
    }

    await flushExpenseOps(summary);
    if (summary.deferredAuth || summary.transient) {
      if (summary.transient && !summary.deferredAuth) {
        backoffMs = backoffMs === 0 ? 2000 : Math.min(MAX_BACKOFF, backoffMs * 2);
        nextAllowedAt = Date.now() + backoffMs;
      }
      return summary;
    }

    try {
      const remote = await fetchCompliance();
      await mergeComplianceFromServer(
        remote.map((r) => ({
          ...r,
          category: r.category as LocalCompliance['category'],
        })),
      );
      await patchSyncFreshness({
        complianceAt: new Date().toISOString(),
        complianceError: null,
      });
    } catch (err) {
      console.warn('compliance merge failed; showing cached data', err);
      await patchSyncFreshness({
        complianceError:
          err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const remote = await fetchExpenses();
      await mergeExpensesFromServer(remote);
      await patchSyncFreshness({
        expensesAt: new Date().toISOString(),
        expensesError: null,
      });
    } catch (err) {
      console.warn('expenses merge failed; showing cached data', err);
      await patchSyncFreshness({
        expensesError: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      const receipts = await fetchReceipts({ limit: '100' });
      await mergeReceiptsFromServer(receipts);
      await patchSyncFreshness({
        receiptsAt: new Date().toISOString(),
        receiptsError: null,
      });
    } catch (err) {
      console.warn('receipts merge failed; showing cached data', err);
      await patchSyncFreshness({
        receiptsError: err instanceof Error ? err.message : String(err),
      });
    }

    try {
      // Full history + future — paginated. Calendar month pulls fill any gaps.
      const jobs = await fetchAllJobs();
      await mergeJobsFromServer(jobs);
      await patchSyncFreshness({
        jobsAt: new Date().toISOString(),
        jobsError: null,
      });
    } catch (err) {
      console.warn('jobs merge failed; showing cached data', err);
      await patchSyncFreshness({
        jobsError: err instanceof Error ? err.message : String(err),
      });
    }

    resetSyncBackoff();
    return summary;
  } catch (err) {
    console.error('flushPendingJobs unexpected failure', err);
    summary.transient = true;
    backoffMs = backoffMs === 0 ? 2000 : Math.min(MAX_BACKOFF, backoffMs * 2);
    nextAllowedAt = Date.now() + backoffMs;
    return summary;
  } finally {
    flushing = false;
  }
}

/** Manual retry of a failed entry — clears fail and requeues. Keeps receipt blobs. */
export async function requeueFailed(id: string): Promise<void> {
  const job = await getJob(id);
  if (job && job.syncState === 'failed' && !job.invoice_run_id) {
    job.syncState = 'pending';
    job.failReason = null;
    job.updatedAt = new Date().toISOString();
    await putJobLocal(job);
    return;
  }
  const receipt = await getReceiptLocal(id);
  if (receipt && receipt.syncState === 'failed') {
    if (receiptPhotoLost(receipt)) {
      receipt.failReason = PHOTO_LOST_REASON;
      receipt.updatedAt = new Date().toISOString();
      await putReceiptLocal(receipt);
      return;
    }
    if (receipt.blob && receipt.blob.size > 0) {
      receipt.syncState = 'pending';
      receipt.failReason = null;
      receipt.updatedAt = new Date().toISOString();
      await putReceiptLocal(receipt);
      return;
    }
  }
  const item = await getCompliance(id);
  if (item && item.syncState === 'failed') {
    item.syncState = 'pending';
    item.failReason = null;
    item.updatedAt = new Date().toISOString();
    await putComplianceLocal(item);
    return;
  }
  const expense = await getExpense(id);
  if (expense && expense.syncState === 'failed') {
    expense.syncState = 'pending';
    expense.failReason = null;
    expense.updatedAt = new Date().toISOString();
    await putExpenseLocal(expense);
  }
}

export function classifyHttpForTests(status: number): 'transient' | 'permanent' {
  if (isPermanentHttp(status)) return 'permanent';
  return 'transient';
}
